import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Hash, Users, Search, Pin, Volume2, Settings, X, Trash2, Shield } from "lucide-react";
import {
  CHANNELS,
  CHANNEL_MESSAGES,
  SERVERS,
  getUserById,
  getInitials,
  getAvatarColor,
  formatTimestamp,
  CURRENT_USER,
} from "@/mock/data";
import { useChatStore } from "@/store/chatStore";
import { joinRoom, leaveRoom, getSocket } from "@/lib/socket";
import { DMInput } from "@/features/dm/DMInput";
import type { ChannelMessage } from "@snowcord/shared";
import "./ChannelView.css";

function ServerSettingsModal({
  serverId,
  onClose,
}: {
  serverId: string;
  onClose: () => void;
}) {
  const server = SERVERS.find((s) => s.id === serverId);
  const [tab, setTab] = useState("overview");
  const [name, setName] = useState(server?.name || "");

  if (!server) return null;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "roles", label: "Roles" },
    { id: "members", label: "Members" },
    { id: "channels", label: "Channels" },
    { id: "invites", label: "Invites" },
    { id: "danger", label: "Danger Zone" },
  ];

  return (
    <div className="server-settings" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="server-settings__backdrop" onClick={onClose} />
      <div className="server-settings__panel" style={{ flexDirection: "row" }}>
        <div className="server-settings__sidebar">
          <div style={{ padding: "8px 12px 16px", fontSize: 14, fontWeight: 600, color: "var(--text)" }}>
            {server.name}
          </div>
          {tabs.map((t) => (
            <button
              key={t.id}
              className={`server-settings__sidebar-item${tab === t.id ? " server-settings__sidebar-item--active" : ""}${t.id === "danger" ? " server-settings__sidebar-item--danger" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="server-settings__content">
          {tab === "overview" && (
            <>
              <div className="server-settings__title">Server Overview</div>
              <div className="server-settings__field">
                <label className="server-settings__label">Server Name</label>
                <input className="server-settings__input" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="server-settings__field">
                <label className="server-settings__label">Invite Code</label>
                <input className="server-settings__input" value={server.inviteCode} readOnly />
              </div>
            </>
          )}
          {tab === "roles" && (
            <>
              <div className="server-settings__title">Roles</div>
              <div style={{ color: "var(--muted)", fontSize: 14 }}>Role management coming soon.</div>
            </>
          )}
          {tab === "members" && (
            <>
              <div className="server-settings__title">Members ({server.memberCount})</div>
              {Array.from({ length: Math.min(server.memberCount, 8) }, (_, i) => (
                <div className="server-settings__member-row" key={i}>
                  <div
                    className="server-settings__member-avatar"
                    style={{ background: getAvatarColor(`member-${i}`) }}
                  >
                    {String.fromCharCode(65 + i)}
                  </div>
                  <div className="server-settings__member-info">
                    <div className="server-settings__member-name">Member {i + 1}</div>
                    <div className="server-settings__member-joined">Joined recently</div>
                  </div>
                </div>
              ))}
            </>
          )}
          {tab === "channels" && (
            <>
              <div className="server-settings__title">Channels</div>
              {CHANNELS.filter((c) => c.serverId === serverId).map((ch) => (
                <div key={ch.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0" }}>
                  {ch.type === "voice" ? <Volume2 size={16} /> : <Hash size={16} />}
                  <span style={{ color: "var(--text)", fontSize: 14 }}>{ch.name}</span>
                </div>
              ))}
            </>
          )}
          {tab === "invites" && (
            <>
              <div className="server-settings__title">Invites</div>
              <div style={{ color: "var(--muted)", fontSize: 14 }}>
                Invite code: <span style={{ color: "var(--accent)" }}>{server.inviteCode}</span>
              </div>
            </>
          )}
          {tab === "danger" && (
            <>
              <div className="server-settings__title" style={{ color: "var(--danger)" }}>Danger Zone</div>
              <button className="server-settings__delete-btn">
                <Trash2 size={14} style={{ display: "inline", verticalAlign: -2 }} /> Delete Server
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ChannelView() {
  const { channelId, serverId } = useParams<{ channelId: string; serverId: string }>();
  const channel = CHANNELS.find((c) => c.id === channelId);
  const messages = channelId ? CHANNEL_MESSAGES[channelId] || [] : [];
  const [showSettings, setShowSettings] = useState(false);
  const [showMemberList, setShowMemberList] = useState(false);

  const chatStore = useChatStore();
  const roomId = channelId ? `channel:${channelId}` : "";

  useEffect(() => {
    if (roomId) joinRoom(roomId);
    return () => { if (roomId) leaveRoom(roomId); };
  }, [roomId]);

  const handleSend = useCallback(
    (content: string) => {
      const socket = getSocket();
      if (!socket || !channelId) return;

      socket.emit("channel:message", { channelId, content });
    },
    [channelId],
  );

  if (!channel) {
    return (
      <div className="channel-view">
        <div className="channel-view__messages">
          <p style={{ color: "var(--muted)", textAlign: "center", marginTop: "40%" }}>
            Select a channel
          </p>
        </div>
      </div>
    );
  }

  let lastSenderId = "";
  let lastDate = "";
  const rendered: React.ReactNode[] = [];

  messages.forEach((msg, i) => {
    const date = new Date(msg.createdAt).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    if (date !== lastDate) {
      lastDate = date;
      lastSenderId = "";
      rendered.push(
        <div className="channel-view__date-divider" key={`date-${i}`}>
          <div className="channel-view__date-line" />
          <span className="channel-view__date-label">{date}</span>
          <div className="channel-view__date-line" />
        </div>,
      );
    }

    const isGroupStart = msg.authorId !== lastSenderId;
    lastSenderId = msg.authorId;
    const author = msg.author;

    rendered.push(
      <div
        className={`channel-view__message${isGroupStart ? " channel-view__message--group-start" : ""}`}
        key={msg.id}
      >
        {isGroupStart ? (
          <div
            className="channel-view__message-avatar"
            style={{ background: getAvatarColor(author.id) }}
            title={author.displayName}
          >
            {getInitials(author.displayName)}
          </div>
        ) : (
          <div className="channel-view__message-avatar-spacer" />
        )}
        <div className="channel-view__message-body">
          {isGroupStart && (
            <div className="channel-view__message-header">
              <span className="channel-view__message-name">{author.displayName}</span>
              <span className="channel-view__message-time">{formatTimestamp(msg.createdAt)}</span>
            </div>
          )}
          <div className="channel-view__message-text">{msg.content}</div>
        </div>
      </div>,
    );
  });

  return (
    <div className="channel-view" style={{ flexDirection: "row" }}>
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
        <div className="channel-view__header">
          <span className="channel-view__header-icon">
            <Hash size={20} />
          </span>
          <span className="channel-view__header-name">{channel.name}</span>
          {channel.topic && (
            <>
              <div className="channel-view__header-divider" />
              <span className="channel-view__header-topic">{channel.topic}</span>
            </>
          )}
          <div className="channel-view__header-actions">
            <button
              className={`channel-view__header-btn${showMemberList ? " channel-view__header-btn--active" : ""}`}
              onClick={() => setShowMemberList(!showMemberList)}
              aria-label="Members"
            >
              <Users size={18} />
            </button>
            <button className="channel-view__header-btn" aria-label="Pinned">
              <Pin size={18} />
            </button>
            <button className="channel-view__header-btn" aria-label="Search">
              <Search size={18} />
            </button>
            {serverId && (
              <button
                className="channel-view__header-btn"
                onClick={() => setShowSettings(true)}
                aria-label="Server settings"
              >
                <Settings size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="channel-view__messages">{rendered}</div>

        <div className="channel-view__input-area">
          <div className="channel-view__input-wrapper">
            <textarea
              className="channel-view__input"
              placeholder={`Message #${channel.name}`}
              rows={1}
            />
          </div>
        </div>
      </div>

      {showSettings && serverId && (
        <ServerSettingsModal serverId={serverId} onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}
